/**
 * Inline lead card — the demo's popup replacement. "Talk to sales" CTAs
 * (.leadcard-toggle) open a tooltip-style card anchored directly below the
 * CTA. With the feature enabled, both .leadcard-toggle and the site's legacy
 * .popup-toggle triggers use this treatment. The card
 * hosts the REAL promo lead form ({$LeadForm} inside window.defaultPopup,
 * per Promo.ss), so markup and native submit stay identical to the
 * existing lead pipeline.
 *
 * DOM discipline: cards are appended to <body> once and the same form node
 * moves between them and the optional hero host. Moving the existing node
 * preserves its listeners and phone widget; the hero host reserves its
 * measured height while the form is borrowed so the page cannot jump. The
 * source popup stays suppressed on these pages (body.popup--hide +
 * popupShown cookie), so it never competes with the embedded form.
 *
 * Mirrors the .popup-toggle conventions from app.js: sets the
 * .popup__triggersource value from data-formtrigger (or clicked:<label>)
 * and the popupShown cookie. If the page has no promo popup, the toggle
 * falls through to its contact-page href.
 */
(function () {
  'use strict';

  // Feature-flagged: Page.ss stamps data-leadcard on <body> when the
  // LeadCard flag is enabled for the locale. Without it this file
  // no-ops — .leadcard-toggle clicks follow their hrefs and the promo
  // popup behaves as standard.
  if (!document.body.hasAttribute('data-leadcard')) {
    return;
  }

  if (window.__leadcard) {
    return;
  }
  window.__leadcard = true;

  document.body.classList.add('popup--hide');

  // Custom CTAs can use only popup-cust-<slug>-toggle, without popup-toggle.
  var CUSTOM_TOGGLE = /(?:^|\s)(popup-cust-[\w-]+)-toggle(?:\s|$)/;
  var toggleSelector = '.leadcard-toggle, .popup-toggle, [class*="popup-cust-"][class*="-toggle"]';
  var card = null;
  var selectionCard = null;
  var inlineHost = document.querySelector('[data-leadcard-host]');
  var defaultForm = null;
  var customForms = {};

  function setCookie(name, value, days) {
    var expires = '';
    if (days) {
      var date = new Date();
      date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
      expires = '; expires=' + date.toGMTString();
    }
    document.cookie = name + '=' + value + expires + '; path=/';
  }

  function customPopupId(el) {
    var match = (el.getAttribute('class') || '').match(CUSTOM_TOGGLE);
    return match ? match[1] : null;
  }

  function isToggle(el) {
    return el.classList.contains('leadcard-toggle') ||
      el.classList.contains('popup-toggle') ||
      !!customPopupId(el);
  }

  function allToggles() {
    return Array.prototype.filter.call(document.querySelectorAll(toggleSelector), isToggle);
  }

  function popupForm(toggle) {
    var id = toggle && customPopupId(toggle);
    if (id) {
      var host = document.getElementById(id);
      var customForm = customForms[id] || (host && host.querySelector('.popup__form'));
      if (customForm) {
        customForms[id] = customForm;
        return customForm;
      }
    }
    if (defaultForm) {
      return defaultForm;
    }
    var selector = typeof window.defaultPopup !== 'undefined' ? window.defaultPopup : '#popup';
    var popup = document.querySelector(selector);
    defaultForm = popup && popup.querySelector('.popup__form');
    return defaultForm;
  }

  // Forms are moved rather than cloned so their listeners and phone widget
  // survive; remember where each came from so swapping between popups can put
  // the previous one back instead of stranding it in the card.
  function rememberOrigin(form) {
    if (form && !form.__leadcardOrigin && form.parentNode) {
      form.__leadcardOrigin = form.parentNode;
    }
  }

  function returnOtherForms(host, keep) {
    var forms = host.querySelectorAll('.popup__form');
    for (var i = 0; i < forms.length; i++) {
      if (forms[i] !== keep && forms[i].__leadcardOrigin) {
        forms[i].__leadcardOrigin.appendChild(forms[i]);
      }
    }
  }

  function reserveInlineHost(form) {
    if (inlineHost && inlineHost.contains(form)) {
      inlineHost.style.minHeight = inlineHost.offsetHeight + 'px';
    }
  }

  function mountInlineForm() {
    if (!inlineHost) {
      return null;
    }
    var form = inlineHost.querySelector('.popup__form') || popupForm();
    if (!form) {
      return null;
    }
    defaultForm = form;
    if (form.parentNode !== inlineHost) {
      inlineHost.appendChild(form);
    }
    inlineHost.style.minHeight = '';
    var source = form.querySelector('.popup__triggersource');
    if (source && inlineHost.getAttribute('data-formtrigger')) {
      source.value = inlineHost.getAttribute('data-formtrigger');
    }
    document.body.classList.add('popup--hide', 'leadcard-embedded');
    inlineHost.classList.add('is-ready');
    return form;
  }

  function ensureCard(toggle) {
    if (card) {
      var existingForm = popupForm(toggle);
      if (existingForm && !card.contains(existingForm)) {
        reserveInlineHost(existingForm);
        rememberOrigin(existingForm);
        var host = card.querySelector('.leadcard__formhost');
        host.appendChild(existingForm);
        returnOtherForms(host, existingForm);
      }
      return card;
    }

    var form = popupForm(toggle);
    if (!form) {
      return null; // no promo popup on this page — toggles fall back to href
    }

    card = document.createElement('div');
    card.className = 'leadcard';
    card.setAttribute('role', 'dialog');
    card.setAttribute('aria-label', 'Request a callback');
    card.innerHTML =
      '<span class="leadcard__notch" aria-hidden="true"></span>' +
      '<div class="leadcard__head">' +
      '<p class="leadcard__title">Request a callback</p>' +
      '<button class="leadcard__close" type="button" aria-label="Close">&times;</button>' +
      '</div>' +
      '<div class="leadcard__formhost typography"></div>';

    // The card is created once; the existing form node may return to the
    // hero host between openings so its state and listeners remain intact.
    reserveInlineHost(form);
    rememberOrigin(form);
    card.querySelector('.leadcard__formhost').appendChild(form);
    document.body.appendChild(card);

    return card;
  }

  function position(targetCard, toggle) {
    var rect = toggle.getBoundingClientRect();
    var viewportWidth = document.documentElement.clientWidth;
    var width = Math.min(400, viewportWidth - 24);
    targetCard.style.width = width + 'px';

    var left = rect.left + window.scrollX;
    var maxLeft = window.scrollX + viewportWidth - width - 12;
    left = Math.min(left, Math.max(window.scrollX + 12, maxLeft));

    targetCard.style.left = Math.round(left) + 'px';

    var top = rect.bottom + window.scrollY + 12;
    var above = false;

    // Never overflow <main>: a toggle near the page bottom (last block
    // above the footer) would push the card past the container and cut
    // the form off. Flip above the CTA when there's room, else clamp.
    var main = document.querySelector('main');
    if (main) {
      var mrect = main.getBoundingClientRect();
      var mainTop = mrect.top + window.scrollY;
      var mainBottom = mrect.bottom + window.scrollY;
      var height = targetCard.offsetHeight; // measurable: closed = visibility, not display

      if (top + height > mainBottom - 12) {
        var flipTop = rect.top + window.scrollY - height - 12;

        if (flipTop >= mainTop + 12) {
          top = flipTop;
          above = true;
        } else {
          top = Math.max(mainTop + 12, mainBottom - 12 - height);
        }
      }
    }

    targetCard.classList.toggle('leadcard--above', above);
    targetCard.style.top = Math.round(top) + 'px';

    // Keep the notch pointing at the toggle's centre.
    var notch = targetCard.querySelector('.leadcard__notch');
    var centre = rect.left + window.scrollX + rect.width / 2 - left;
    notch.style.left = Math.round(Math.min(Math.max(centre, 18), width - 30)) + 'px';
  }

  function salesFallbackLink() {
    var toggles = allToggles();
    var fallback = '';
    for (var i = 0; i < toggles.length; i++) {
      if (!toggles[i].hasAttribute('href') || toggles[i].closest('.leadcard')) {
        continue;
      }
      if (toggles[i].textContent.toLowerCase().indexOf('talk to sales') !== -1) {
        return toggles[i].href;
      }
      var link = new URL(toggles[i].href, window.location.href);
      if (!fallback && link.pathname !== window.location.pathname) {
        fallback = toggles[i].href;
      }
    }
    return fallback;
  }

  function ensureSelectionCard() {
    if (selectionCard) {
      return selectionCard;
    }

    selectionCard = document.createElement('div');
    selectionCard.className = 'leadcard leadcard--selection typography';
    selectionCard.setAttribute('role', 'dialog');
    selectionCard.setAttribute('aria-label', 'Selected product options');
    selectionCard.setAttribute('data-formtrigger', 'selected-product');
    selectionCard.innerHTML =
      '<span class="leadcard__notch" aria-hidden="true"></span>' +
      '<div class="leadcard__selection-options">' +
      '<div class="leadcard__head">' +
      '<div><p class="leadcard__eyebrow">Ready for the next step</p>' +
      '<p class="leadcard__title">Your setup is selected</p></div>' +
      '<button class="leadcard__close leadcard__selection-close" type="button" aria-label="Close">&times;</button>' +
      '</div>' +
      '<p class="leadcard__selection-copy">Continue to checkout, or talk it through with our team.</p>' +
      '<div class="leadcard__selection-actions">' +
      '<a class="button button--small leadcard__review" href="#"><span>Checkout</span></a>' +
      '<a class="button button--small button--secondary popup-toggle leadcard-toggle leadcard__sales" href="#"><span>Talk to sales</span></a>' +
      '</div>' +
      '</div>' +
      '<div class="leadcard__selection-form">' +
      '<div class="leadcard__head">' +
      '<button class="leadcard__back" type="button"><span aria-hidden="true">&larr;</span> Back</button>' +
      '<button class="leadcard__close leadcard__selection-close" type="button" aria-label="Close">&times;</button>' +
      '</div>' +
      '<p class="leadcard__title">Request a callback</p>' +
      '<div class="leadcard__formhost typography"></div>' +
      '</div>';
    document.body.appendChild(selectionCard);

    return selectionCard;
  }

  function showSelection(button) {
    if (!button || !button.href) {
      return;
    }

    var selected = ensureSelectionCard();
    resetSelectionForm();
    var salesLink = salesFallbackLink();
    if (selected.__toggle && selected.__toggle !== button) {
      selected.__toggle.setAttribute('aria-expanded', 'false');
    }
    selected.querySelector('.leadcard__review').href = button.href;
    selected.querySelector('.leadcard__sales').href = salesLink || button.href;
    selected.classList.add('is-open');
    selected.__toggle = button;
    button.setAttribute('aria-expanded', 'true');
    position(selected, button);
  }

  function resetSelectionForm() {
    if (!selectionCard || !selectionCard.classList.contains('leadcard--form')) {
      return;
    }
    var form = selectionCard.querySelector('.popup__form');
    if (form && selectionCard.__formOrigin) {
      selectionCard.__formOrigin.appendChild(form);
      if (selectionCard.__formOrigin === inlineHost) {
        inlineHost.style.minHeight = '';
      }
    }
    selectionCard.classList.remove('leadcard--form');
    selectionCard.__formOrigin = null;
    var selectionToggle = selectionCard.__toggle;
    if (selectionToggle) {
      requestAnimationFrame(function () {
        if (selectionCard.classList.contains('is-open')) {
          position(selectionCard, selectionToggle);
        }
      });
    }
  }

  function openSelectionForm(toggle) {
    var selected = toggle.closest('.leadcard--selection');
    var form = popupForm();
    if (!selected || !form) {
      return false;
    }

    if (card && card.contains(form)) {
      close();
    }
    reserveInlineHost(form);
    selected.__formOrigin = form.parentNode;
    selected.querySelector('.leadcard__formhost').appendChild(form);
    selected.classList.add('leadcard--form');

    var source = form.querySelector('.popup__triggersource');
    if (source) {
      source.value = selected.getAttribute('data-formtrigger');
    }
    setCookie('popupShown', 'true', 0.025);

    var first = form.querySelector('input[name="FirstName"], input[name="Name"]') ||
      form.querySelector('input:not([type="hidden"]):not(.h), select, textarea');
    if (first) {
      setTimeout(function () {
        first.focus({ preventScroll: true });
      }, 300);
    }

    requestAnimationFrame(function () {
      position(selected, selected.__toggle);
      keepFormInView(selected);
    });
    setTimeout(function () {
      keepFormInView(selected);
    }, 300);
    return true;
  }

  function closeSelection() {
    if (!selectionCard || !selectionCard.classList.contains('is-open')) {
      return;
    }
    resetSelectionForm();
    selectionCard.classList.remove('is-open');
    if (selectionCard.__toggle) {
      selectionCard.__toggle.setAttribute('aria-expanded', 'false');
      selectionCard.__toggle.focus({ preventScroll: true });
      selectionCard.__toggle = null;
    }
  }

  function keepFormInView(targetCard) {
    targetCard = targetCard || card;
    if (!targetCard || !targetCard.classList.contains('is-open')) {
      return;
    }

    var form = targetCard.querySelector('.leadcard__formhost');
    var rect = form.getBoundingClientRect();
    var viewport = window.visualViewport;
    var top = (viewport ? viewport.offsetTop : 0) + 12;
    var bottom = top + (viewport ? viewport.height : window.innerHeight) - 24;

    if (rect.bottom > top && rect.top < bottom) {
      return;
    }

    var delta = rect.height <= bottom - top
      ? rect.top + rect.height / 2 - (top + bottom) / 2
      : rect.top - top;

    window.scrollBy({
      top: delta,
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
    });
  }

  function openInline(toggle) {
    var form = mountInlineForm();
    if (!form) {
      return false;
    }

    var triggerEl = toggle.closest('[data-formtrigger]');
    var source = form.querySelector('.popup__triggersource');
    if (source) {
      source.value = (triggerEl && triggerEl.getAttribute('data-formtrigger')) ||
        'clicked:' + toggle.innerText.trim();
    }
    setCookie('popupShown', 'true', 0.025);

    var rect = inlineHost.getBoundingClientRect();
    var viewport = window.visualViewport;
    var top = (viewport ? viewport.offsetTop : 0) + 12;
    var bottom = top + (viewport ? viewport.height : window.innerHeight) - 24;
    if (rect.bottom <= top || rect.top >= bottom) {
      inlineHost.scrollIntoView({
        block: rect.height <= bottom - top ? 'center' : 'start',
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
      });
    }

    var first = form.querySelector('input[name="FirstName"], input[name="Name"]') ||
      form.querySelector('input:not([type="hidden"]):not(.h), select, textarea');
    if (first) {
      setTimeout(function () {
        first.focus({ preventScroll: true });
      }, 300);
    }
    return true;
  }

  function open(toggle) {
    if (inlineHost && toggle.hasAttribute('data-leadcard-inline')) {
      close();
      return openInline(toggle);
    }
    if (!ensureCard(toggle)) {
      return false;
    }

    position(card, toggle);

    // Same conventions as app.js's .popup-toggle handler.
    var triggerEl = toggle.closest('[data-formtrigger]');
    var source = card.querySelector('.popup__triggersource');
    if (source) {
      source.value = (triggerEl && triggerEl.getAttribute('data-formtrigger')) ||
        'clicked:' + toggle.innerText.trim();
    }
    setCookie('popupShown', 'true', 0.025);

    card.classList.add('is-open');
    card.__toggle = toggle;
    toggle.setAttribute('aria-expanded', 'true');

    // Focus First Name (or the single Name field variant) so typing can
    // start immediately; generic fallback skips hiddens and the honeypot.
    var first = card.querySelector('input[name="FirstName"], input[name="Name"]') ||
      card.querySelector('input:not([type="hidden"]):not(.h), select, textarea');
    if (first) {
      first.focus({ preventScroll: true });
    }
    requestAnimationFrame(function () {
      keepFormInView();
    });
    setTimeout(keepFormInView, 300);
    return true;
  }

  mountInlineForm();

  function close() {
    if (!card || !card.classList.contains('is-open')) {
      return;
    }
    card.classList.remove('is-open');
    if (inlineHost) {
      mountInlineForm();
    }
    if (card.__toggle) {
      card.__toggle.setAttribute('aria-expanded', 'false');
      card.__toggle.focus({ preventScroll: true });
      card.__toggle = null;
    }
  }

  document.addEventListener('click', function (e) {
    var toggle = e.target.closest(toggleSelector);
    if (toggle && isToggle(toggle)) {
      if (toggle.classList.contains('leadcard__sales') && openSelectionForm(toggle)) {
        e.preventDefault();
        e.stopImmediatePropagation();
        return;
      }
      var isOpenHere = card && card.classList.contains('is-open') && card.__toggle === toggle;
      if (isOpenHere) {
        e.preventDefault();
        e.stopImmediatePropagation();
        close();
        return;
      }
      if (open(toggle)) {
        e.preventDefault();
        e.stopImmediatePropagation();
      }
      return;
    }

    if (e.target.closest('.leadcard__back')) {
      resetSelectionForm();
      selectionCard.querySelector('.leadcard__sales').focus({ preventScroll: true });
      return;
    }

    if (e.target.closest('.leadcard__close')) {
      if (e.target.closest('.leadcard__selection-close')) {
        closeSelection();
      } else {
        close();
      }
      return;
    }

    // Tooltip behaviour: clicking outside dismisses.
    if (card && card.classList.contains('is-open') && !e.target.closest('.leadcard')) {
      close();
    }
  }, true);

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') {
      close();
    }
  });

  window.addEventListener('resize', function () {
    if (card && card.classList.contains('is-open') && card.__toggle) {
      position(card, card.__toggle);
    }
    if (selectionCard && selectionCard.classList.contains('is-open') && selectionCard.__toggle) {
      position(selectionCard, selectionCard.__toggle);
    }
  });

  window.addEventListener('epos:product-selected', function (event) {
    showSelection(event.detail && event.detail.button);
  });

  // The form submits natively and Silverstripe redirects back to the page —
  // both for success (FormHasSubmitted thank-you) and validation failures
  // (sessionMessage + field errors re-rendered in the form). Remember which
  // CTA the submission came from so the reload can scroll back to it and
  // reopen the card with that feedback visible.
  document.addEventListener('submit', function (e) {
    if (!card || !card.contains(e.target)) {
      return;
    }
    var toggles = allToggles();
    var index = toggles.indexOf(card.__toggle);
    try {
      sessionStorage.setItem('leadcard_return', String(index));
    } catch (err) {
      /* private mode */
    }
  });

  function reopenAfterRedirect() {
    var stored = null;
    try {
      stored = sessionStorage.getItem('leadcard_return');
      sessionStorage.removeItem('leadcard_return');
    } catch (err) {
      return;
    }
    if (stored === null) {
      return;
    }

    var selector = typeof window.defaultPopup !== 'undefined' ? window.defaultPopup : '#popup';
    var popup = document.querySelector(selector);
    var feedback = popup &&
      popup.querySelector('.popup__form .form-message, .popup__form form .message');
    if (!feedback) {
      return;
    }

    // The feedback belongs to the card — make sure the (form-less) popup
    // shell isn't showing, whatever raced us to the submitfail param.
    popup.classList.remove('show');

    var toggles = allToggles();
    var toggle = toggles[parseInt(stored, 10)] || toggles[0];
    if (!toggle) {
      return;
    }
    toggle.scrollIntoView({ block: 'center' });
    open(toggle);
  }

  reopenAfterRedirect();
})();
